import { resolvePersonaId, type PublicUser } from '@evi/shared';

/**
 * The Today inbox's presentation (Warren, 21 October 2026, item M: "Today inbox with counts"). The Api decides every row
 * and who sees it (GET /admin/today, apps/api/src/lib/adminToday.ts); this only orders and words them. Pure, so the
 * badge and the rows are tested without a browser.
 */

export const TODAY_PATH = '/today';
/** The Super Admin lands on Today after sign-in: the persona whose menu carries the Today leaf (config/superAdminNav.ts). */
export const landsOnToday = (user: Partial<Pick<PublicUser, 'role' | 'template' | 'is_sales_agent'>> | null | undefined) =>
  !!user && resolvePersonaId({ role: user.role ?? null, template: user.template ?? null, is_sales_agent: user.is_sales_agent ?? false }) === 'super_admin';
export interface TodayRow {
  key: string; label: string; count: number; overdue: number; escalated?: number;
  oldest_since: string | null; link: string; in_total: boolean;
}
export interface TodayInbox { rows: TodayRow[]; total: number }

/** The bell is the screen that clears its own rows: the page opens it rather than navigating. */
export const BELL_LINK = '#bell';
export const OPEN_BELL_EVENT = 'evi:open-bell';

/** The Today leaf's badge: the total waiting; zero shows no badge. */
export function todayBadge(total: number | null | undefined): string | null {
  if (!total || total < 1) return null;
  return total > 99 ? '99+' : String(total);
}

/** What is waiting, oldest first where a row has an age, then the larger counts. */
export function waitingRows(rows: readonly TodayRow[]): TodayRow[] {
  return rows.filter(r => r.count > 0).sort((a, b) => {
    if (a.oldest_since && b.oldest_since) return +new Date(a.oldest_since) - +new Date(b.oldest_since);
    if (a.oldest_since || b.oldest_since) return a.oldest_since ? -1 : 1;
    return b.count - a.count || a.label.localeCompare(b.label);
  });
}

/** The rows with nothing waiting, by name, for the "Clear" line. */
export const clearRows = (rows: readonly TodayRow[]) => rows.filter(r => r.count === 0).map(r => r.label);

/** "3 hours", "4 days": how long the oldest has waited. */
export function waitedFor(since: string, now = new Date()): string {
  const h = Math.max(0, Math.round((+now - +new Date(since)) / 3_600_000));
  return h < 48 ? `${h} hour${h === 1 ? '' : 's'}` : `${Math.round(h / 24)} days`;
}

/** The row's line under its name: its age, what is past EVI's promise, what is escalated. */
export function rowDetail(row: TodayRow, now = new Date()): string {
  const parts: string[] = [];
  if (row.oldest_since) parts.push(`oldest waiting ${waitedFor(row.oldest_since, now)}`);
  if (row.overdue) parts.push(row.key === 'service_calls' ? `${row.overdue} past the installer's answer time` : `${row.overdue} past the one business day promise`);
  if (row.escalated) parts.push(`${row.escalated} with EVI ops`);
  if (!row.in_total) parts.push('not in the total: the bell has its own badge');
  return parts.join(', ');
}
